import { ChartCard, type ChartCardState } from "../../ChartCard";
import type { ModelUsage } from "../../../../hooks/useAdminStatus";
import { rangeWindowLabel, type InstanceRange } from "../../../../lib/instanceDetailRange";
import { hasEstimatedRow, localModelRows } from "./localModelRows";
import styles from "./LocalModelsPanel.module.css";

type LocalModelsPanelProps = {
  readonly range: InstanceRange;
  readonly status: "loading" | "ready" | "error";
  readonly usage: ModelUsage | null;
  readonly error: string | null;
};

const cardState = (
  status: LocalModelsPanelProps["status"],
  rowCount: number,
): ChartCardState => {
  if (status === "loading") return "loading";
  if (status === "error") return "error";
  if (rowCount === 0) return "empty";
  return "ready";
};

// Usage of the models that run on the tenant's own box rather than through a
// provider - whisper.cpp for voice notes, llama.cpp for memory-search
// embeddings, both installed by the ztc-local-models role. This is the panel
// that answers "is the box actually doing this work, or silently falling back
// to OpenRouter?", so an empty state is a real signal, not just missing data.
export const LocalModelsPanel = ({ range, status, usage, error }: LocalModelsPanelProps): JSX.Element => {
  const rows = localModelRows(usage);
  return (
    <ChartCard
      title="On-box models"
      subtitle={rows.length > 0 ? `${rows.length} active ${rangeWindowLabel(range)}` : undefined}
      state={cardState(status, rows.length)}
      emptyText="No on-box model activity in this window."
      hint="Whisper (voice notes) and llama.cpp (embeddings) running on the box. Empty while the tenant sends voice notes or writes memories means the local models may not be installed."
      errorText={error ?? undefined}
    >
      <div className={styles.scroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Model</th>
            <th>Runs</th>
            <th>Workload</th>
            <th>Avg time</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key}>
              <td>
                <div className={styles.kindCell}>
                  <span className={styles.kind}>
                    {row.kindLabel}
                    {row.engine != null && <span className={styles.engine}> · {row.engine}</span>}
                  </span>
                  <span className={styles.model}>{row.model}</span>
                </div>
              </td>
              <td className={styles.num}>{row.runs}</td>
              <td className={styles.num}>
                {row.workload}
                {row.workloadEstimated && <span className={styles.estimateMark}>est.</span>}
              </td>
              <td className={styles.num}>{row.avgTime}</td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {hasEstimatedRow(rows) && (
        <p className={styles.footnote} title="OpenClaw embeds in-process, so the sampler counts new embedding-cache rows every 15 minutes and multiplies by the chunk size.">
          Token counts marked est. are estimated; run counts are exact.
        </p>
      )}
    </ChartCard>
  );
};
